
import { useState, useEffect } from 'react';

interface AlertProps {
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message?: string;
  isOpen: boolean;
  onClose: () => void;
  autoClose?: boolean;
  duration?: number;
}

export function Alert({ 
  type, 
  title, 
  message, 
  isOpen, 
  onClose, 
  autoClose = false, 
  duration = 3000 
}: AlertProps) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      if (autoClose) {
        const timer = setTimeout(() => {
          handleClose();
        }, duration);
        return () => clearTimeout(timer);
      }
    }
  }, [isOpen, autoClose, duration]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  if (!isOpen) return null;

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <i className="ri-check-circle-line text-2xl text-green-600"></i>;
      case 'error':
        return <i className="ri-error-warning-line text-2xl text-red-600"></i>;
      case 'warning':
        return <i className="ri-alert-line text-2xl text-yellow-600"></i>;
      case 'info':
        return <i className="ri-information-line text-2xl text-blue-600"></i>;
      default:
        return <i className="ri-information-line text-2xl text-blue-600"></i>;
    }
  };

  const getColors = () => {
    switch (type) {
      case 'success':
        return 'bg-green-50 border-green-200';
      case 'error':
        return 'bg-red-50 border-red-200';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200';
      case 'info':
        return 'bg-blue-50 border-blue-200';
      default:
        return 'bg-blue-50 border-blue-200';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`bg-white rounded shadow-xl max-w-md w-full transform transition-all duration-300 ${
        isVisible ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
      }`}>
        <div className={`p-6 border-l-4 ${getColors()}`}>
          <div className="flex items-start">
            <div className="flex-shrink-0">
              {getIcon()}
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {title}
              </h3>
              {message && (
                <p className="text-sm text-gray-600 mb-4">
                  {message}
                </p>
              )}
              <div className="flex justify-end">
                <button
                  type='button'
                  onClick={handleClose}
                  className="px-4 py-1 w-full bg-emerald-600 text-white rounde hover:bg-emerald-700 transition-colors whitespace-nowrap"
                >
                  Compris
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

interface ConfirmProps {
  title: string;
  message?: string;
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  confirmText?: string;
  cancelText?: string;
}

export function Confirm({ 
  title, 
  message, 
  isOpen, 
  onConfirm, 
  onCancel, 
  confirmText = 'Confirmer',
  cancelText = 'Annuler'
}: ConfirmProps) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
    }
  }, [isOpen]);

  const handleConfirm = () => {
    setIsVisible(false);
    setTimeout(() => {
      onConfirm();
    }, 300);
  };

  const handleCancel = () => {
    setIsVisible(false);
    setTimeout(() => {
      onCancel();
    }, 300);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`bg-white rounded shadow-xl max-w-md w-full transform transition-all duration-300 ${
        isVisible ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
      }`}>
        <div className="p-6">
          <div className="flex items-center mb-4">
            <i className="ri-question-line text-2xl text-yellow-600 mr-3"></i>
            <h3 className="text-lg font-medium text-gray-900">
              {title}
            </h3>
          </div>
          {message && (
            <p className="text-sm text-gray-600 mb-6">
              {message}
            </p>
          )}
          <div className="flex gap-3 justify-end">
            <button
              type='button'
              onClick={handleCancel}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
            >
              {cancelText}
            </button>
            <button
              type='button'
              onClick={handleConfirm}
              className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors whitespace-nowrap"
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

interface ToastProps {
  type: 'success' | 'error' | 'warning' | 'info';
  message: string;
  isOpen: boolean;
  onClose: () => void;
  duration?: number;
}

export function Toast({ type, message, isOpen, onClose, duration = 3000 }: ToastProps) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      const timer = setTimeout(() => {
        handleClose();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [isOpen, duration]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  if (!isOpen) return null;

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <i className="ri-check-circle-line text-green-600"></i>;
      case 'error':
        return <i className="ri-error-warning-line text-red-600"></i>;
      case 'warning':
        return <i className="ri-alert-line text-yellow-600"></i>;
      case 'info':
        return <i className="ri-information-line text-blue-600"></i>;
      default:
        return <i className="ri-information-line text-blue-600"></i>;
    }
  };

  const getColors = () => {
    switch (type) {
      case 'success':
        return 'bg-green-50 border-green-200';
      case 'error':
        return 'bg-red-50 border-red-200';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200';
      case 'info':
        return 'bg-blue-50 border-blue-200';
      default:
        return 'bg-blue-50 border-blue-200';
    }
  };

  return (
    <div className="fixed top-4 right-4 z-50">
      <div className={`bg-white rounded-lg shadow-lg border-l-4 ${getColors()} p-4 max-w-sm transform transition-all duration-300 ${
        isVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
      }`}>
        <div className="flex items-center">
          <div className="flex-shrink-0">
            {getIcon()}
          </div>
          <div className="ml-3 flex-1">
            <p className="text-sm font-medium text-gray-900">
              {message}
            </p>
          </div>
          <button
            type={'button'}
            onClick={handleClose}
            className="ml-3 text-gray-400 hover:text-gray-600"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>
      </div>
    </div>
  );
}
